import { cilFingerprint } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  faCheck,
  faCog,
  faCopy,
  faEllipsisH,
  faExclamationTriangle,
  faHourglassStart,
  faPercent,
  faTrashAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useState } from "react";
import ReactTooltip from "react-tooltip";
import styles from "./attachment.module.css";

const Attachment = ({ attachment, handleDeleteAttachment, hasError, isUploadingAttachment, uploadProgress }) => {
  const [hasCopied, setHasCopied] = useState(false);
  const { _id: attachmentId, filename, hash = [], size } = attachment;
  const sha256 = hash.find(h => h.algorithm === "sha256")?.value ?? "";
  const fileExt = filename.split(".").slice(-1).pop().toLowerCase();

  let friendlySize = size;
  const units = ["bytes", "KB", "MB", "GB", "TB"];
  while (friendlySize >= 1024 && units.length > 1) {
    friendlySize = ~~(friendlySize / 102.4) / 10;
    units.shift();
  }
  friendlySize += "\u00A0" + units[0];

  const isFinalizing = isUploadingAttachment && Number.parseFloat(uploadProgress) === 100;
  const isQueued = isUploadingAttachment && uploadProgress == null;

  const handleCopyClick = async e => {
    e.stopPropagation();
    setHasCopied(true);
    await navigator.clipboard.writeText(sha256);
    setTimeout(() => setHasCopied(false), 1000);
  };

  const tooltip = (
    <ReactTooltip
      className="mw-100"
      clickable
      effect="solid"
      globalEventOff="click"
      id={attachmentId}
      overridePosition={({ left, top }, currentEvent, currentTarget, node) => {
        const d = document.documentElement;
        left = Math.min(d.clientWidth - node.clientWidth, left);
        top = Math.min(d.clientHeight - node.clientHeight, top);
        left = Math.max(0, left);
        top = Math.max(0, top);
        return { top, left };
      }}
    >
      <div onClick={e => e.stopPropagation()}>
        <div>Filename: {filename}</div>
        {size ? <div>Size: {friendlySize}</div> : null}
        {sha256 ? (
          <div className="align-items-center d-flex flex-wrap">
            <CIcon className="fa-w-14 fa-fw mr-1" icon={cilFingerprint} />
            SHA256:&nbsp;
            <samp className="font-weight-bold mr-2" style={{ wordBreak: "break-all" }}>
              {sha256}
            </samp>
            <button
              aria-label="Copy hash to clipboard"
              className={styles.btnCopy}
              onClick={handleCopyClick}
              title="Copy hash to clipboard"
            >
              <FontAwesomeIcon fixedWidth icon={hasCopied ? faCheck : faCopy} />
            </button>
          </div>
        ) : null}
      </div>
    </ReactTooltip>
  );

  return (
    <li
      key={attachmentId}
      className="align-items-stretch bg-transparent border-0 d-flex list-group-item mb-1 p-0"
      onClick={e => e.stopPropagation()}
    >
      <span className="mr-2 text-truncate" title={filename}>
        {filename} {size ? `(${friendlySize})` : null}
      </span>
      <button
        className={classNames(styles.btnSm, "align-self-center mr-2")}
        data-for={attachmentId}
        data-tip
        data-event="click"
        data-iscapture="true"
        onClick={e => e.stopPropagation()}
      >
        <FontAwesomeIcon icon={faEllipsisH} />
      </button>
      <span
        className={classNames(
          styles.ext,
          "align-items-center bg-dark d-flex ml-auto mx-2 rounded semi-bold user-select-none"
        )}
      >
        {fileExt}
      </span>
      <div
        className={classNames(
          styles.fileProgress,
          hasError
            ? "badge-danger"
            : isQueued
              ? "badge-secondary"
              : isFinalizing || isUploadingAttachment
                ? "badge-primary"
                : "badge-success",
          "align-items-center badge d-flex flex-row justify-content-center mr-2 p-1"
        )}
      >
        {hasError ? (
          <FontAwesomeIcon fixedWidth icon={faExclamationTriangle} />
        ) : isFinalizing ? (
          <FontAwesomeIcon fixedWidth icon={faCog} spin />
        ) : isQueued ? (
          <FontAwesomeIcon fixedWidth icon={faHourglassStart} />
        ) : isUploadingAttachment ? (
          <span>
            {uploadProgress} <FontAwesomeIcon fixedWidth icon={faPercent} />
          </span>
        ) : (
          <FontAwesomeIcon className="text-light" fixedWidth icon={faCheck} />
        )}
      </div>
      <button className={classNames(styles.btnDel)} onClick={handleDeleteAttachment}>
        <FontAwesomeIcon fixedWidth icon={faTrashAlt} />
      </button>
      {tooltip}
    </li>
  );
};

Attachment.propTypes = {
  attachment: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    hash: PropTypes.arrayOf(
      PropTypes.shape({
        algorithm: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ),
    size: PropTypes.number,
    status: PropTypes.string
  }).isRequired,
  handleDeleteAttachment: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  isUploadingAttachment: PropTypes.bool,
  uploadProgress: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default Attachment;
